import { DateTime } from 'luxon';
import { utils, writeFile, type WorkBook } from 'xlsx-js-style';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { getResolvedBuildingVersion } from '@shared/helpers/actions/resolved-version.helpers';
import { getProjectGfa } from '@shared/helpers/project.helpers';
import { getProposalsInVersion } from '@shared/helpers/proposal.helpers';
import { getAllBuildingVersions } from '@shared/helpers/recursive-element.helpers';
import {
  filterResultsByLifecycles,
  getLifecycleLabel,
} from '@shared/helpers/lifecycles.helpers';
import { getResultsRecord } from '@shared/helpers/results.helpers';
import { getBuildingLevelResults } from '@shared/helpers/results/building-level-results.helpers';
import { getSbefGroupResults } from '@shared/helpers/sbef-results.helpers';
import { buildAbsolutePartialProjectUrl } from '@shared/helpers/router.helpers';
import type { IProposal } from '@shared/models/proposals.model';
import { type Baseline, type Project } from '@shared/models/project.model';
import type { Recipe } from '@shared/models/recipe.model';
import { Lifecycle } from '@shared/models/lifecycles.model';
import type { Results, ResultsRecord } from '@shared/models/unit.model';
import {
  allocateExportWorksheetTabName,
  applyExportOverviewPresentation,
  getExportSpreadsheetProductElements,
  versionToSheet,
} from './export.helpers';

const BASELINE_EXPORT_LABEL = 'Baseline';
const OVERVIEW_METADATA_ROW_COUNT = 7;
/** Blank spacer row between Scope metadata and the proposal table. */
const OVERVIEW_SPACER_ROW = OVERVIEW_METADATA_ROW_COUNT;
const OVERVIEW_HEADER_ROW = OVERVIEW_SPACER_ROW + 1;
const OVERVIEW_HEADERS = [
  'Sheet name',
  'kgCO2e total',
  'kgCO2e per m2',
] as const;
/** ~15% wider than previous 18 / 13 for columns A and B. */
const OVERVIEW_COLUMN_WIDTHS = [21, 15, 13] as const;
const TIMESTAMP_FORMAT = 'yyyy-MM-dd';

export type ExportProjectId = number | undefined;
export type ExportProjectSource = 'store' | 'fetch+enrich' | 'enrich';

export type ResolveProjectForSpreadsheetExportDeps = {
  getLoadedProject: () => Project | undefined;
  fetchProjectById: (id: number) => Promise<Project | undefined>;
  enrichProject: (project: Project, recipes: Recipe[]) => Promise<Project>;
};

/**
 * Resolves the project document used for spreadsheet export.
 * Reuses the in-memory store project when it is already enriched and matches
 * {@link projectId} (or when no id is requested), avoiding a refetch + worker enrich.
 * Store accessors are injected via {@link deps} so this module never binds them
 * at import time (avoids TDZ from circular store imports).
 */
export const resolveProjectForSpreadsheetExport = async (
  projectId: ExportProjectId,
  recipes: Recipe[],
  deps: ResolveProjectForSpreadsheetExportDeps,
): Promise<{
  project: Project | undefined;
  source: ExportProjectSource;
}> => {
  const loaded = deps.getLoadedProject();
  if (loaded?.enriched && (projectId == null || loaded.id === projectId)) {
    return { project: loaded, source: 'store' };
  }

  if (projectId != null && loaded?.id !== projectId) {
    const fetched = await deps.fetchProjectById(projectId);
    if (!fetched) return { project: undefined, source: 'fetch+enrich' };
    if (fetched.enriched) {
      return { project: fetched, source: 'fetch+enrich' };
    }
    return {
      project: await deps.enrichProject(fetched, recipes),
      source: 'fetch+enrich',
    };
  }

  if (!loaded) return { project: undefined, source: 'enrich' };
  if (loaded.enriched) return { project: loaded, source: 'store' };

  return {
    project: await deps.enrichProject(loaded, recipes),
    source: 'enrich',
  };
};

export type BuildExportSpreadsheetParams = {
  project: Project;
  source: ExportProjectSource;
  organizationName: string;
  contactPersonName: string;
  /** GFA from the open UI project; used when {@link source} is not `fetch+enrich`. */
  storeGfa: number;
  selectedLifecycles: Lifecycle[];
  selectedVersionId?: string;
  /** `window.location.origin` (or test stand-in) for the Overview Project URL. */
  origin: string;
  /** Preformatted date string (`yyyy-MM-dd`) for Overview + file name. */
  timestamp: string;
};

/**
 * Builds the export workbook (Overview + baseline/proposal resource sheets).
 * Returns `undefined` when the project has no exportable version or results.
 */
export const buildExportSpreadsheet = ({
  project,
  source,
  organizationName,
  contactPersonName,
  storeGfa,
  selectedLifecycles,
  selectedVersionId,
  origin,
  timestamp,
}: BuildExportSpreadsheetParams):
  { workbook: WorkBook; fileName: string } | undefined => {
  const versions = getAllBuildingVersions(project);
  // Prefer the UI-selected version when it belongs to this project; else first version.
  const versionToExport =
    versions.find((version) => version.id === selectedVersionId) ?? versions[0];
  if (!versionToExport) return undefined;

  // Fetched projects are not the open UI project — derive GFA from the document.
  const currentGfa =
    source === 'fetch+enrich' ? getProjectGfa(project) : storeGfa;

  // Deep link via routing helpers so list export never embeds the projects-list URL.
  const projectUrl = buildAbsolutePartialProjectUrl(origin, {
    organization: organizationName,
    projectId: String(project.id),
  });

  const workbook = utils.book_new();

  const indexRows: (string | number)[][] = [
    ['Project name', project.name],
    ['GFA (m²)', Math.round(currentGfa)],
    ['Company name', organizationName],
    ['Contact person', contactPersonName],
    ['Date', timestamp],
    ['Project URL', projectUrl],
    ['Scope', getLifecycleLabel(selectedLifecycles)],
    [''],
    [...OVERVIEW_HEADERS],
  ];
  const indexSheet = utils.aoa_to_sheet(indexRows);
  utils.book_append_sheet(workbook, indexSheet, 'Overview');

  const usedWorksheetTabNames = new Set<string>(['Overview']);

  /**
   * Adds one Overview row and a resource worksheet for baseline or a single proposal.
   */
  const addResourceSheet = (
    version: Baseline,
    worksheetTabBase: string,
    rollupFactors: Results | undefined,
    elementResultsRecord: ResultsRecord,
    proposal?: IProposal,
  ) => {
    if (!rollupFactors) {
      console.error(
        'No rollup factors for export scenario',
        worksheetTabBase,
        version.id,
      );
      return;
    }

    const filteredRollup = filterResultsByLifecycles(
      rollupFactors,
      selectedLifecycles,
    );
    // Resolve once, then reuse for collect + row path lookups.
    const pathVersion = proposal
      ? getResolvedBuildingVersion(version, proposal)
      : version;

    const activeElements = getExportSpreadsheetProductElements(
      version,
      elementResultsRecord,
      proposal,
      pathVersion,
    ).filter((element) => elementResultsRecord[element.id]);
    if (!activeElements.length) return;

    const co2eTotalKg = getConversionFactorValue(filteredRollup, 'co2e');
    const co2ePerM2 = currentGfa ? co2eTotalKg / currentGfa : 0;
    const sheetName = allocateExportWorksheetTabName(
      usedWorksheetTabNames,
      worksheetTabBase,
    );

    indexRows.push([sheetName, Math.round(co2eTotalKg), Math.round(co2ePerM2)]);

    const sheet = versionToSheet({
      version,
      resultsRecord: elementResultsRecord,
      selectedLifecycles,
      elements: activeElements,
      pathVersion,
      marketEstimates: project.marketEstimates,
      // Recomputed from the same helper the pipeline uses, since the stored version row
      // mixes these into the "Other" SBEF bucket and cannot be split back out.
      // These buckets are the ContentCoverage-scaled ones, while the pipeline gates BD7/BD8
      // on the unscaled buckets. Equivalent because coverage only ever multiplies by
      // 1/coverage for coverage ∈ (0,1) — a positive factor that cannot zero out the mass
      // or A-stage emissions the gating tests for.
      buildingLevelResults: getBuildingLevelResults(
        project,
        getSbefGroupResults(rollupFactors),
      ),
    });

    utils.book_append_sheet(workbook, sheet, sheetName);
  };

  addResourceSheet(
    versionToExport,
    BASELINE_EXPORT_LABEL,
    versionToExport.resultsRecord[versionToExport.id],
    getResultsRecord(versionToExport),
  );

  getProposalsInVersion(versionToExport).forEach((proposal) => {
    addResourceSheet(
      versionToExport,
      proposal.name,
      proposal.resultsRecord[versionToExport.id],
      getResultsRecord(versionToExport, proposal),
      proposal,
    );
  });

  // Rebuild Overview after resource sheets have appended index rows
  const updatedIndexSheet = utils.aoa_to_sheet(indexRows);
  applyExportOverviewPresentation(updatedIndexSheet, {
    metadataRowCount: OVERVIEW_METADATA_ROW_COUNT,
    headerRow: OVERVIEW_HEADER_ROW,
    headerColumnCount: OVERVIEW_HEADERS.length,
    rowCount: indexRows.length,
    columnWidths: OVERVIEW_COLUMN_WIDTHS,
  });

  workbook.Sheets.Overview = updatedIndexSheet;

  return {
    workbook,
    fileName: `${project.name.slice(0, 50)} ${timestamp}.xlsx`,
  };
};

export type ExportProjectSpreadsheetParams = {
  projectId?: ExportProjectId;
  recipes: Recipe[];
  organizationName: string | undefined;
  contactPersonName: string | undefined;
  storeGfa: number;
  selectedLifecycles: Lifecycle[];
  selectedVersionId?: string;
  /** `window.location.origin` (or test stand-in) for the Overview Project URL. */
  origin: string;
  /** Defaults to today (`yyyy-MM-dd`). Inject in tests for stable file names. */
  timestamp?: string;
  resolveDeps: ResolveProjectForSpreadsheetExportDeps;
  /**
   * Defaults to `xlsx` {@link writeFile}. Inject a no-op / spy in tests.
   */
  writeWorkbook?: (workbook: WorkBook, fileName: string) => void;
};

/**
 * Resolves the project, builds the spreadsheet workbook, and writes the `.xlsx` file.
 * No-ops when required context (project, org, contact) is missing.
 */
export const exportProjectSpreadsheet = async ({
  projectId,
  recipes,
  organizationName,
  contactPersonName,
  storeGfa,
  selectedLifecycles,
  selectedVersionId,
  origin,
  timestamp = DateTime.now().toFormat(TIMESTAMP_FORMAT),
  resolveDeps,
  writeWorkbook = writeFile,
}: ExportProjectSpreadsheetParams): Promise<
  { workbook: WorkBook; fileName: string } | undefined
> => {
  const { project, source } = await resolveProjectForSpreadsheetExport(
    projectId,
    recipes,
    resolveDeps,
  );
  if (!project || !organizationName || !contactPersonName) return undefined;

  const built = buildExportSpreadsheet({
    project,
    source,
    organizationName,
    contactPersonName,
    storeGfa,
    selectedLifecycles,
    selectedVersionId,
    origin,
    timestamp,
  });
  if (!built) return undefined;

  writeWorkbook(built.workbook, built.fileName);
  return built;
};
